import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, unref as _unref, createVNode as _createVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "modal-overlay" }
const _hoisted_2 = { class: "modal-content" }
const _hoisted_3 = { class: "modal-header" }
const _hoisted_4 = { class: "modal-body" }
const _hoisted_5 = { class: "form-container" }
const _hoisted_6 = { class: "form-input" }
const _hoisted_7 = {
  for: "overrideKnockouts",
  class: "checkbox-label"
}
const _hoisted_8 = ["disabled", "required"]
const _hoisted_9 = { class: "list-columns-container" }
const _hoisted_10 = { class: "list-column" }
const _hoisted_11 = {
  key: 0,
  class: "skeleton"
}
const _hoisted_12 = { key: 1 }
const _hoisted_13 = { class: "list-column" }
const _hoisted_14 = {
  key: 0,
  class: "skeleton"
}
const _hoisted_15 = { key: 1 }
const _hoisted_16 = { class: "form-input" }
const _hoisted_17 = {
  for: "overrideScore",
  class: "checkbox-label"
}
const _hoisted_18 = ["disabled", "required"]
const _hoisted_19 = { class: "list-columns-container" }
const _hoisted_20 = { class: "list-column" }
const _hoisted_21 = {
  key: 0,
  class: "skeleton"
}
const _hoisted_22 = { key: 1 }
const _hoisted_23 = { key: 2 }
const _hoisted_24 = { class: "list-column" }
const _hoisted_25 = {
  key: 0,
  class: "skeleton"
}
const _hoisted_26 = { key: 1 }
const _hoisted_27 = { key: 2 }
const _hoisted_28 = {
  key: 0,
  class: "error-container"
}
const _hoisted_29 = { class: "modal-footer" }
const _hoisted_30 = ["disabled"]
const _hoisted_31 = ["disabled"]

import { onMounted, onUnmounted, ref, computed } from 'vue';
	import axios from 'axios';
	import functions from '@/use/functions';
	import Spinner from '@/components/shared/Spinner.vue';
	import { OverrideCreditDetails, AnnualReviewOverride } from '@/models/loans';
	import { ExclamationCircleIcon } from '@heroicons/vue/24/solid';

	
export default /*@__PURE__*/_defineComponent({
  __name: 'OverrideModal',
  props: {
    annualReviewId: {},
    loanId: {},
    overrides: {},
    creditPullTypeId: {},
    onClose: { type: Function },
    onOverrideSuccessAsync: { type: Function }
  },
  setup(__props: any) {

	const props = __props;

	const isLoading = ref<boolean>(false);
	const isSubmitting = ref<boolean>(false);
	const errorProcessing = ref<boolean>(false);

	const creditDetails = ref<OverrideCreditDetails | null>(null);

	const overrideKnockouts = ref<boolean>(false);
	const overrideScore = ref<boolean>(false);

	const canOverride = computed<boolean>(() => props.overrides.length === 0);

	const requiresKnockoutsOverride = computed(
		() => borrowerKnockouts.value.length > 0 || coBorrowerKnockouts.value.length > 0
	);

	const requiresScoreOverride = computed(
		() => borrowerScoreKnockout.value !== null || coBorrowerScoreKnockout.value !== null
	);

	const isFormValid = computed(() => {
		const hasKnockoutsOverride = requiresKnockoutsOverride.value && overrideKnockouts.value;
		const hasScoreOverride = requiresScoreOverride.value && overrideScore.value;

		return hasKnockoutsOverride || hasScoreOverride;
	});

	const isKnockoutCheckboxDisabled = computed(
		() => !requiresKnockoutsOverride.value || isLoading.value || isSubmitting.value || !canOverride.value
	);
	const isScoreCheckboxDisabled = computed(
		() => !requiresScoreOverride.value || isLoading.value || isSubmitting.value || !canOverride.value
	);

	const borrowerKnockouts = computed(
		() => creditDetails.value?.borrowerKnockouts.filter((k) => k.knockoutTypeId !== 22) || []
	);

	const coBorrowerKnockouts = computed(
		() => creditDetails.value?.coBorrowerKnockouts.filter((k) => k.knockoutTypeId !== 22) || []
	);

	const borrowerScoreKnockout = computed(
		() => creditDetails.value?.borrowerKnockouts.find((k) => k.knockoutTypeId === 22) || null
	);

	const coBorrowerScoreKnockout = computed(
		() => creditDetails.value?.coBorrowerKnockouts.find((k) => k.knockoutTypeId === 22) || null
	);

	function handleClose() {
		if (isSubmitting.value) {
			return;
		}

		props.onClose();
	}

	async function overrideAnnualReviewAsync() {
		isSubmitting.value = true;
		errorProcessing.value = false;

		try {
			const queryParams = {
				loanId: props.loanId,
				overrideKnockouts: overrideKnockouts.value,
				overrideScore: overrideScore.value
			};

			await axios.post(`/api/annualreview/${props.annualReviewId}/override`, null, { params: queryParams });

			await props.onOverrideSuccessAsync();
		} catch {
			errorProcessing.value = true;
		} finally {
			isSubmitting.value = false;
		}
	}

	async function fetchOverrideDetailsAsync() {
		isLoading.value = true;

		try {
			const queryParams = {
				creditPullTypeId: props.creditPullTypeId
			};

			const response = await axios.get<OverrideCreditDetails>(
				`/api/annualreview/${props.annualReviewId}/override`,
				{ params: queryParams }
			);

			creditDetails.value = response.data;
		} catch {
			functions.openModal({
				title: 'Oops!',
				description: `There was an issue getting the override details. Please try again later.`
			});
		} finally {
			isLoading.value = false;
		}
	}

	onMounted(async () => {
		document.body.style.overflow = 'hidden';

		// get initial overrides if they exist
		overrideKnockouts.value = props.overrides.some((o) => o.overrideTypeId === 1);
		overrideScore.value = props.overrides.some((o) => o.overrideTypeId === 2);

		await fetchOverrideDetailsAsync();
	});

	onUnmounted(() => {
		document.body.style.overflow = '';
	});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[2] || (_cache[2] = _createElementVNode("h3", null, "Annual Review Override", -1)),
        _createElementVNode("span", {
          class: _normalizeClass(["close", { disabled: isSubmitting.value }]),
          onClick: handleClose
        }, "×", 2)
      ]),
      _createElementVNode("div", _hoisted_4, [
        _cache[12] || (_cache[12] = _createElementVNode("p", null, "Before performing an override, please confirm the override reasons.", -1)),
        _createElementVNode("form", _hoisted_5, [
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("label", _hoisted_7, [
              _withDirectives(_createElementVNode("input", {
                type: "checkbox",
                id: "overrideKnockouts",
                name: "overrideKnockouts",
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((overrideKnockouts).value = $event)),
                disabled: isKnockoutCheckboxDisabled.value,
                required: requiresKnockoutsOverride.value
              }, null, 8, _hoisted_8), [
                [_vModelCheckbox, overrideKnockouts.value]
              ]),
              _cache[3] || (_cache[3] = _createElementVNode("span", { class: "custom-checkbox" }, null, -1)),
              _cache[4] || (_cache[4] = _createTextVNode(" Override annual review knockouts ", -1))
            ])
          ]),
          _createElementVNode("div", _hoisted_9, [
            _createElementVNode("div", _hoisted_10, [
              _cache[5] || (_cache[5] = _createTextVNode(" Borrower ", -1)),
              _createElementVNode("ul", null, [
                (isLoading.value)
                  ? (_openBlock(), _createElementBlock("li", _hoisted_11))
                  : (borrowerKnockouts.value.length === 0)
                    ? (_openBlock(), _createElementBlock("li", _hoisted_12, "N/A"))
                    : _createCommentVNode("", true),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(borrowerKnockouts.value, (knockout) => {
                  return (_openBlock(), _createElementBlock("li", {
                    key: knockout.knockoutTypeId
                  }, [
                    _createTextVNode(_toDisplayString(knockout.knockoutName) + " | ", 1),
                    _createElementVNode("span", {
                      class: _normalizeClass(knockout.decisionCssClass)
                    }, _toDisplayString(knockout.decision), 3)
                  ]))
                }), 128))
              ])
            ]),
            _createElementVNode("div", _hoisted_13, [
              _cache[6] || (_cache[6] = _createTextVNode(" Co-borrower ", -1)),
              _createElementVNode("ul", null, [
                (isLoading.value)
                  ? (_openBlock(), _createElementBlock("li", _hoisted_14))
                  : (coBorrowerKnockouts.value.length === 0)
                    ? (_openBlock(), _createElementBlock("li", _hoisted_15, "N/A"))
                    : _createCommentVNode("", true),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(coBorrowerKnockouts.value, (knockout) => {
                  return (_openBlock(), _createElementBlock("li", {
                    key: knockout.knockoutTypeId
                  }, [
                    _createTextVNode(_toDisplayString(knockout.knockoutName) + " | ", 1),
                    _createElementVNode("span", {
                      class: _normalizeClass(knockout.decisionCssClass)
                    }, _toDisplayString(knockout.decision), 3)
                  ]))
                }), 128))
              ])
            ])
          ]),
          _createElementVNode("div", _hoisted_16, [
            _createElementVNode("label", _hoisted_17, [
              _withDirectives(_createElementVNode("input", {
                type: "checkbox",
                id: "overrideScore",
                name: "overrideScore",
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((overrideScore).value = $event)),
                disabled: isScoreCheckboxDisabled.value,
                required: requiresScoreOverride.value
              }, null, 8, _hoisted_18), [
                [_vModelCheckbox, overrideScore.value]
              ]),
              _cache[7] || (_cache[7] = _createElementVNode("span", { class: "custom-checkbox" }, null, -1)),
              _cache[8] || (_cache[8] = _createTextVNode(" Override annual review score ", -1))
            ])
          ]),
          _createElementVNode("div", _hoisted_19, [
            _createElementVNode("div", _hoisted_20, [
              _cache[9] || (_cache[9] = _createTextVNode(" Borrower ", -1)),
              _createElementVNode("ul", null, [
                (isLoading.value)
                  ? (_openBlock(), _createElementBlock("li", _hoisted_21))
                  : (!borrowerScoreKnockout.value)
                    ? (_openBlock(), _createElementBlock("li", _hoisted_22, "N/A"))
                    : (_openBlock(), _createElementBlock("li", _hoisted_23, [
                        _createTextVNode(_toDisplayString(borrowerScoreKnockout.value.value) + " | ", 1),
                        _createElementVNode("span", {
                          class: _normalizeClass(borrowerScoreKnockout.value.decisionCssClass)
                        }, _toDisplayString(borrowerScoreKnockout.value.decision), 3)
                      ]))
              ])
            ]),
            _createElementVNode("div", _hoisted_24, [
              _cache[10] || (_cache[10] = _createTextVNode(" Co-borrower ", -1)),
              _createElementVNode("ul", null, [
                (isLoading.value)
                  ? (_openBlock(), _createElementBlock("li", _hoisted_25))
                  : (!coBorrowerScoreKnockout.value)
                    ? (_openBlock(), _createElementBlock("li", _hoisted_26, "N/A"))
                    : (_openBlock(), _createElementBlock("li", _hoisted_27, [
                        _createTextVNode(_toDisplayString(coBorrowerScoreKnockout.value.value) + " | ", 1),
                        _createElementVNode("span", {
                          class: _normalizeClass(coBorrowerScoreKnockout.value.decisionCssClass)
                        }, _toDisplayString(coBorrowerScoreKnockout.value.decision), 3)
                      ]))
              ])
            ])
          ])
        ]),
        _cache[13] || (_cache[13] = _createElementVNode("p", null, [
          _createTextVNode(" By clicking the "),
          _createElementVNode("i", null, "“Override”"),
          _createTextVNode(" button, I am overriding the systematic annual review decision for this line of credit. ")
        ], -1)),
        (errorProcessing.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_28, [
              _createVNode(_unref(ExclamationCircleIcon), { class: "error-icon" }),
              _cache[11] || (_cache[11] = _createElementVNode("p", { class: "error" }, " The override failed to update the LOC for Annual Review. Please close this and try again later. ", -1))
            ]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_29, [
        (isSubmitting.value)
          ? (_openBlock(), _createBlock(Spinner, { key: 0 }))
          : _createCommentVNode("", true),
        _createElementVNode("button", {
          class: "secondary",
          onClick: handleClose,
          disabled: isSubmitting.value
        }, "Cancel", 8, _hoisted_30),
        _createElementVNode("button", {
          onClick: overrideAnnualReviewAsync,
          disabled: !isFormValid.value || isSubmitting.value || !creditDetails.value || !canOverride.value
        }, " Override ", 8, _hoisted_31)
      ])
    ])
  ]))
}
}

})